import React from 'react'
import { Space, Tag } from 'antd'
import { ArrowRightOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import Text from 'antd/es/typography/Text'
import { parseJsonValue, formatBooleanValue } from '../utils/changeValueParser'
import { useThemeColors } from '../../../../../../../hooks/useThemeColors'
import PatternIcon from '../../../../../../../components/common/PatternIcon'

/**
 * Display component for booster/tool changes
 */
export const BoosterChangeDisplay = ({ oldValue, newValue, changeType, activeBoosters }) => {
    const oldData = parseJsonValue(oldValue)
    const newData = parseJsonValue(newValue)

    const renderBoosterInfo = data => {
        if (!data) return null

        // Find the booster in activeBoosters to get displayName
        const booster = activeBoosters?.find(b => b.id === data.booster_id)
        const displayName = booster?.displayName || data.booster_name || `Tool ${data.booster_id}`
        const priority = data.priority

        return (
            <Space size={4}>
                <Tag color='blue'>{displayName}</Tag>
                {priority && <Text type='secondary'>(Priority {priority})</Text>}
            </Space>
        )
    }

    if (changeType === 'booster_priority') {
        return (
            <Space size={4}>
                <Text strong>{oldValue}</Text>
                <ArrowRightOutlined />
                <Text strong>{newValue}</Text>
            </Space>
        )
    }

    // For add/remove, show only the entity, not "None -> X" or "X -> None"
    if (changeType === 'booster_added') {
        return renderBoosterInfo(newData)
    }

    if (changeType === 'booster_removed') {
        return renderBoosterInfo(oldData)
    }

    // For other changes, show old -> new
    return (
        <Space size={4}>
            {renderBoosterInfo(oldData) || 'None'}
            <ArrowRightOutlined />
            {renderBoosterInfo(newData) || 'None'}
        </Space>
    )
}

/**
 * Display component for conceptual label changes
 * Styled like ConceptualLabelSelect with disposition icon inside the tag
 */
export const ConceptualLabelChangeDisplay = ({ oldValue, newValue, changeType }) => {
    const colors = useThemeColors()
    const oldData = parseJsonValue(oldValue)
    const newData = parseJsonValue(newValue)

    const renderConceptualLabelInfo = data => {
        if (!data) return null

        const name = data.conceptual_label_name || `CL ${data.conceptual_label_id}`
        const disposition = data.disposition

        return (
            <Tag>
                <Space size={4}>
                    {disposition !== undefined &&
                        (disposition ? (
                            <ArrowUpOutlined style={{ color: colors.success }} />
                        ) : (
                            <ArrowDownOutlined style={{ color: colors.error }} />
                        ))}
                    <span>{name}</span>
                </Space>
            </Tag>
        )
    }

    // For add/remove, show only the entity
    if (changeType === 'conceptual_label_added') {
        return renderConceptualLabelInfo(newData)
    }

    if (changeType === 'conceptual_label_removed') {
        return renderConceptualLabelInfo(oldData)
    }

    // For other changes, show old -> new
    return (
        <Space size={4}>
            {renderConceptualLabelInfo(oldData) || 'None'}
            <ArrowRightOutlined />
            {renderConceptualLabelInfo(newData) || 'None'}
        </Space>
    )
}

/**
 * Display component for pattern/category creation
 */
export const PatternCreatedDisplay = ({ newValue }) => {
    const data = parseJsonValue(newValue)

    if (!data) return null

    const patternId = data.pattern_id
    const patternName = data.pattern_name || `Pattern ${patternId}`
    const patternGrade = data.pattern_grade
    const patternDescription = data.pattern_description

    return (
        <Space size={4}>
            <PatternIcon id={patternId} colorId={patternId} content={patternGrade} suffix='pt' />
            <Text strong>{patternName}</Text>
            {patternDescription && <Text type='secondary'>- {patternDescription}</Text>}
        </Space>
    )
}

/**
 * Display component for color changes
 */
export const ColorChangeDisplay = ({ oldValue, newValue, colors }) => {
    const renderColorInfo = colorId => {
        if (colorId === null || colorId === undefined || colorId === 'null') return 'None'

        const id = Number(colorId)
        const color = colors?.find(c => c.id === id)

        if (color) {
            return (
                <Space size={4}>
                    <PatternIcon
                        id={id}
                        colorId={color.id}
                        suffix='pt'
                        showId={false}
                    />
                    <Text>{color.label || `Color ${id}`}</Text>
                </Space>
            )
        }

        return <Text>Color {id}</Text>
    }

    return (
        <Space size={4}>
            {renderColorInfo(oldValue)}
            <ArrowRightOutlined />
            {renderColorInfo(newValue)}
        </Space>
    )
}

/**
 * Display component for boolean changes
 */
export const BooleanChangeDisplay = ({ oldValue, newValue }) => {
    return (
        <Space size={4}>
            <Text strong>{formatBooleanValue(oldValue)}</Text>
            <ArrowRightOutlined />
            <Text strong>{formatBooleanValue(newValue)}</Text>
        </Space>
    )
}

/**
 * Default display component for simple value changes
 */
export const DefaultChangeDisplay = ({ oldValue, newValue }) => {
    return (
        <Space size={4}>
            {oldValue || 'None'}
            <ArrowRightOutlined />
            {newValue || 'None'}
        </Space>
    )
}

